<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="$i18n.t('table.attributeStats.title', { value: layerAttribute?.label })"
    :has-agree="false"
    disagree-text="dialog.close"
    dialog-width="30%"
    min-body-height="500px"
    header-shadow
  >
    <template #body>
      <v-col cols="12" class="text-body-2">
        <v-row no-gutters>
          <v-col cols="auto">
            <v-checkbox
              class="ml-0 pl-0"
              v-model="withSelected"
              @change="getStatsValues"
              :disabled="!currentLayerSelectedFeatures?.length || loading || !isFetched"
            >
              <template #label>
                <div
                  class="text-body-2 text-left"
                  :class="{ 'label-dark': currentLayerSelectedFeatures?.length && !loading && isFetched }"
                >
                  {{ $i18n.t('table.attributeStats.withSelected') }}
                </div>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>
        <main-loader v-if="loading" />
        <template v-else>
          <v-row no-gutters v-for="(stat, idx) of statsMetadata" :key="idx" class="pb-5">
            <template v-if="stat.name === 'unique_values'">
              <v-col cols="12" class="top-border pt-5">
                <v-row
                  no-gutters
                  v-for="([key, value], idx) of Object.entries(statsValues?.unique_values || {})"
                  :key="idx"
                  class="pb-5"
                >
                  <v-col cols="7" class="text-left pr-1"> {{ key }}: </v-col>
                  <v-col cols="5" class="text-left pl-1">
                    <span> {{ value.count }} </span>
                    <span class="italic--text font-italic"> {{ `(${value.percentage}%)` }} </span>
                  </v-col>
                </v-row>
              </v-col>
            </template>
            <template v-else>
              <v-col cols="7" class="text-left pr-1"> {{ stat.label }}: </v-col>
              <v-col cols="5" class="text-left pl-1">
                {{ statsValues?.[stat.name] }}
              </v-col>
            </template>
          </v-row>
        </template>
      </v-col>
    </template>
  </main-large-dialog>
</template>
<script>
import { call, get } from 'vuex-pathify';

import MainLargeDialog from '@/components/MainLargeDialog';
import MainLoader from '@/components/MainLoader';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'AttributeStatsDialog',
  components: { MainLargeDialog, MainLoader },
  mixins: [prepareAttributesFeatureCardMixin],
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    attributeData: {
      type: Object,
    },
    currentLayerSelectedFeatures: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },
  computed: {
    layers: get('layers/layers'),
    datasources: get('layers/metadata'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    attributeName() {
      return this.attributeData?.attribute;
    },
    layerId() {
      return this.attributeData?.layerId;
    },
    layer() {
      return this.layers[this.layerId];
    },
    layerAttributes() {
      return this.$_flattenStructure(this.getForm(this.layer?.form_schema?.elements || [], this.datasourceAttributes));
    },
    layerAttribute() {
      return this.layerAttributes.find(attr => attr.attribute === this.attributeName);
    },
    layerFilterExpression: get('layers/layersFilters@:layerId@filterExpression'),
    datasourceName() {
      return this.layer?.data_source_name;
    },
    datasource() {
      return this.datasources[this.datasourceName];
    },
    datasourceAttributes() {
      return this.datasource?.attributes_schema?.attributes || [];
    },
    datasourceAttribute() {
      return this.datasourceAttributes.find(attr => attr.name === this.attributeName);
    },
    datasourceIdAttribute() {
      return this.datasource?.attributes_schema.id_name;
    },
    datasourceAttributeDataTypeName() {
      return this.datasourceAttribute?.data_type.name;
    },
    statsMetadata() {
      const defaultStats = [
        {
          name: 'verbose_name',
          label: this.$i18n.t('table.attributeStats.stats.verboseName'),
        },
        {
          name: 'name',
          label: this.$i18n.t('table.attributeStats.stats.name'),
        },
        {
          name: 'number_of_nulls',
          label: this.$i18n.t('table.attributeStats.stats.numberOfNulls'),
        },
      ];
      const numberStats = [
        {
          name: 'sum',
          label: this.$i18n.t('table.attributeStats.stats.sum'),
          formatter: value => (!value && value !== 0 ? '' : parseFloat(value.toFixed(4))),
        },
        {
          name: 'mean',
          label: this.$i18n.t('table.attributeStats.stats.mean'),
          formatter: value => (!value && value !== 0 ? '' : parseFloat(value.toFixed(4))),
        },
        {
          name: 'median',
          label: this.$i18n.t('table.attributeStats.stats.median'),
          formatter: value => (!value && value !== 0 ? '' : parseFloat(value.toFixed(4))),
        },
        {
          name: 'min',
          label: this.$i18n.t('table.attributeStats.stats.min'),
          formatter: value => (!value && value !== 0 ? '' : parseFloat(value.toFixed(4))),
        },
        {
          name: 'max',
          label: this.$i18n.t('table.attributeStats.stats.max'),
          formatter: value => (!value && value !== 0 ? '' : parseFloat(value.toFixed(4))),
        },
      ];
      const textStats = [
        {
          name: 'number_of_unique_values',
          label: this.$i18n.t('table.attributeStats.stats.numberOfUniqueValues'),
        },
      ];
      const dateStats = [
        {
          name: 'interval',
          label: this.$i18n.t('table.attributeStats.stats.interval'),
          formatter: value => (!value && value !== 0 ? '' : parseFloat((value / 86400).toFixed(0))),
        },
        {
          name: 'min',
          label: this.$i18n.t('table.attributeStats.stats.minDate'),
          formatter: (value, dt) => (!value ? '' : this.$dayjs(value).format(dt === 'datetime' ? 'L, LTS' : 'L')),
        },
        {
          name: 'max',
          label: this.$i18n.t('table.attributeStats.stats.maxDate'),
          formatter: (value, dt) => (!value ? '' : this.$dayjs(value).format(dt === 'datetime' ? 'L, LTS' : 'L')),
        },
      ];
      const timeStats = [
        {
          name: 'min',
          label: this.$i18n.t('table.attributeStats.stats.min'),
          formatter: value => (!value ? '' : value.substring(0, 5)),
        },
        {
          name: 'max',
          label: this.$i18n.t('table.attributeStats.stats.max'),
          formatter: value => (!value ? '' : value.substring(0, 5)),
        },
      ];
      const uniqueValuesStats = [
        {
          name: 'unique_values',
        },
      ];
      return [
        ...defaultStats,
        ...(['text', 'boolean', 'hyperlink', 'date', 'datetime', 'time'].includes(this.datasourceAttributeDataTypeName)
          ? textStats
          : []),
        ...(['integer', 'decimal', 'float'].includes(this.datasourceAttributeDataTypeName) ? numberStats : []),
        ...(['date', 'datetime'].includes(this.datasourceAttributeDataTypeName) ? dateStats : []),
        ...(['time'].includes(this.datasourceAttributeDataTypeName) ? timeStats : []),
        ...(['text', 'boolean', 'hyperlink'].includes(this.datasourceAttributeDataTypeName) ? uniqueValuesStats : []),
      ];
    },
  },
  data: () => ({
    isFetched: false,
    loading: false,
    statsValues: {},
    withSelected: false,
  }),
  methods: {
    getDataSourceAttributeStatistics: call('layers/getDataSourceAttributeStatistics'),
    async getStatsValues() {
      this.loading = true;
      try {
        const filters = [
          ...(this.layerFilterExpression ? [this.layerFilterExpression] : []),
          ...(this.withSelected
            ? [
                {
                  $IN: {
                    [this.datasource?.attributes_use_datasource_qualified_names
                      ? this.datasourceIdAttribute
                      : `${this.datasourceName}.${this.datasourceIdAttribute}`]: this.currentLayerSelectedFeatures,
                  },
                },
              ]
            : []),
        ];
        let filterExpression = {};
        if (filters.length === 1) filterExpression = filters[0];
        else if (filters.length > 1) filterExpression = { '!AND': filters };
        const statusValues = await this.getDataSourceAttributeStatistics({
          dataSourceName: this.datasourceName,
          attributeName: this.attributeName,
          params: {
            features_filter: filterExpression,
          },
        });
        this.statsValues = Object.freeze({
          ...statusValues,
          ...Object.entries(statusValues).reduce((total, [statName, statValue]) => {
            const statMetadata = this.statsMetadata.find(stat => stat.name === statName);
            if (statMetadata?.formatter)
              total[statName] = statMetadata.formatter(statValue, this.datasourceAttributeDataTypeName);
            else total[statName] = statValue;
            return total;
          }, {}),
          verbose_name: this.layerAttribute?.label,
          name: this.attributeName,
        });
        this.isFetched = true;
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    computedIsVisible: {
      handler(nV) {
        this.withSelected = false;
        this.statsValues = {};
        if (nV) this.getStatsValues();
      },
      immediate: true,
    },
  },
};
</script>
<style lang="scss" scoped>
.label-dark {
  color: #202124;
}
.top-border {
  @include border(top, 1px);
}
</style>
